import { useEffect, useState } from "react";
import { ArrowLeft, Mail, Phone, UserRound } from "lucide-react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchUserById } from "../../../Profile/redux/thunks/userThunks";
import { resolvePatientPublicIdentifier } from "../../../consultation/components/FilePage/PatientProfileCard/patientPublicIdentifier";
import PaymentTab from "../../../consultation/components/FilePage/PaymentTab/PaymentTab";
import useAssistantDoctorId from "../../../assistantDashboard/hooks/useAssistantDoctorId";
import AppLayoutAssistant from "../../../navigation/Layout/LayoutAssistant";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import "./PatientPaymentHistoryPage.css";

export default function PatientPaymentHistoryPage({ role = "assistant" }) {
  const { patientId } = useParams();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const assistantDoctorId = useAssistantDoctorId();
  const subprofileId = searchParams.get("subprofileId") || undefined;
  const [patient, setPatient] = useState(location.state?.patient || null);
  const [loadingPatient, setLoadingPatient] = useState(!location.state?.patient);
  const isDoctor = role === "doctor";
  const isRTL = i18n.dir(i18n.resolvedLanguage || i18n.language) === "rtl";
  const Layout = isDoctor ? AppLayoutDoctor : AppLayoutAssistant;

  useEffect(() => {
    if (!patientId) return undefined;
    let active = true;
    setLoadingPatient(true);
    dispatch(fetchUserById({
      userId: subprofileId || patientId,
      principalUserId: subprofileId ? patientId : undefined,
      subprofileId,
    }))
      .unwrap()
      .then((result) => {
        if (active) setPatient((previous) => ({ ...previous, ...result }));
      })
      .catch((error) => {
        console.error("[PatientPaymentHistory] failed to load patient", error);
      })
      .finally(() => {
        if (active) setLoadingPatient(false);
      });

    return () => {
      active = false;
    };
  }, [dispatch, patientId, subprofileId]);

  const patientName = patient?.fullName || patient?.name ||
    [patient?.firstName, patient?.lastName].filter(Boolean).join(" ") || "—";
  const patientReference = resolvePatientPublicIdentifier(patient, patient) || patient?.ptn || "";
  const patientPhone = patient?.phone || patient?.phoneNumber || patient?.telephone || "—";
  const patientEmail = patient?.email || "—";
  const handleBack = () => {
    const returnTo = location.state?.returnTo || searchParams.get("returnTo");
    if (typeof returnTo === "string" && returnTo.startsWith("/")) {
      navigate(returnTo);
      return;
    }
    if (window.history.state?.idx > 0) {
      navigate(-1);
      return;
    }
    navigate(isDoctor ? "/patients" : "/assistant/patients");
  };

  return (
    <Layout>
      <main className="patient-payment-history-page" dir={isRTL ? "rtl" : "ltr"}>
        <header className="patient-payment-header">
          <button
            type="button"
            className="patient-payment-back"
            onClick={handleBack}
            aria-label={t("common.back", { defaultValue: "Back" })}
          >
            <ArrowLeft size={22} aria-hidden="true" />
          </button>
          <div className="patient-payment-heading">
            <h1>{t("patientPaymentHistory.title")}</h1>
            <p>
              {t("patientPaymentHistory.patientAccount")} · {patientName}
              {patientReference ? ` · ${patientReference}` : ""}
            </p>
          </div>
        </header>

        <section className="patient-payment-contact" aria-label={t("assistantPaymentHistory.contactsTitle")}>
          {loadingPatient && !patient ? (
            <p>{t("assistantPaymentHistory.loadingContact")}</p>
          ) : (
            <dl>
              <div>
                <dt><UserRound size={14} aria-hidden="true" />{t("assistantPaymentHistory.patient")}</dt>
                <dd>{patientName}</dd>
              </div>
              <div>
                <dt><Phone size={14} aria-hidden="true" />{t("assistantPaymentHistory.phone")}</dt>
                <dd>{patientPhone}</dd>
              </div>
              <div>
                <dt><Mail size={14} aria-hidden="true" />{t("assistantPaymentHistory.email")}</dt>
                <dd>{patientEmail}</dd>
              </div>
            </dl>
          )}
        </section>

        <PaymentTab
          patientId={patientId}
          patientName={patientName}
          patientReference={patientReference}
          subprofileId={subprofileId}
          fullPage
          role={role}
          doctorId={isDoctor ? undefined : assistantDoctorId}
        />
      </main>
    </Layout>
  );
}